import * as z from "zod";
import { formatDurationMs } from "@/lib/utils";

const ProofCaseStatus = z.enum([
  "pending",
  "running",
  "observed",
  "blocked",
  "skipped",
  "not_run",
]);
export type ProofCaseStatus = z.infer<typeof ProofCaseStatus>;

const ProofCaseReason = z.enum([
  "credentials",
  "network",
  "environment",
  "flaky",
  "other",
]);
export type ProofCaseReason = z.infer<typeof ProofCaseReason>;

const ProofEvalsRowStatus = z.enum(["passed", "failed", "skipped"]);
export type ProofEvalsRowStatus = z.infer<typeof ProofEvalsRowStatus>;

const ProofEvalsCounts = z.object({
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative().nullable().optional(),
});
export type ProofEvalsCounts = z.infer<typeof ProofEvalsCounts>;

const ProofEvalsRow = z.object({
  name: z.string().max(200),
  status: ProofEvalsRowStatus,
  base_status: ProofEvalsRowStatus.nullable().optional(),
  score: z.number().nullable().optional(),
});
export type ProofEvalsRow = z.infer<typeof ProofEvalsRow>;

const ProofEvalsResult = ProofEvalsCounts.extend({
  runs: z.number().int().positive().nullable().optional(),
  base: ProofEvalsCounts.nullable().optional(),
  rows: z.array(ProofEvalsRow).max(50).nullable().optional(),
});
export type ProofEvalsResult = z.infer<typeof ProofEvalsResult>;

const ProofEvidence = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("image"),
    url: z.string(),
    label: z.string().nullable().optional(),
  }),
  z.object({ kind: z.literal("link"), url: z.string(), label: z.string() }),
  z.object({
    kind: z.literal("evals"),
    url: z.string(),
    label: z.string(),
    result: ProofEvalsResult,
  }),
]);
export type ProofEvidence = z.infer<typeof ProofEvidence>;

/** A case's result on an older head, kept when recheck_case reopens it. */
const ProofCaseAttempt = z.object({
  status: ProofCaseStatus,
  head_sha: z.string().nullable(),
  observed: z.string().nullable(),
  reason: ProofCaseReason.nullable(),
});

// head_sha and attempts are optional so reports settled before runs tracked heads still parse.
const ProofCase = z.object({
  id: z.string(),
  title: z.string(),
  claim: z.string(),
  hero: z.boolean(),
  status: ProofCaseStatus,
  observed: z.string().nullable(),
  reason: ProofCaseReason.nullable(),
  evidence: z.array(ProofEvidence),
  started_at: z.string().nullable(),
  closed_at: z.string().nullable(),
  head_sha: z.string().nullable().optional(),
  attempts: z.array(ProofCaseAttempt).optional(),
});
export type ProofCase = z.infer<typeof ProofCase>;

const ProofFinding = z.object({
  title: z.string(),
  severity: z.enum(["issue", "note"]),
  observed: z.string(),
  evidence: z.array(ProofEvidence),
  head_sha: z.string().nullable().optional(),
});
export type ProofFinding = z.infer<typeof ProofFinding>;

/** The backend's ProofRunSnapshot on the wire: live frames, `active_proof_runs`, and the settled `proof_report`. */
export const ProofRunSnapshot = z.object({
  child_session_id: z.string(),
  pr_url: z.string(),
  pr_number: z.number(),
  status: z.enum(["planning", "running", "finished", "aborted"]),
  aborted_reason: z.string().nullable(),
  head_sha: z.string().nullable().optional(),
  cases: z.array(ProofCase),
  findings: z.array(ProofFinding),
});
export type ProofRunSnapshot = z.infer<typeof ProofRunSnapshot>;

export interface ProofPanelCase {
  id: string;
  title: string;
  claim: string;
  hero: boolean;
  status: ProofCaseStatus;
  observed?: string;
  reason?: ProofCaseReason;
  evidence: ProofEvidence[];
  duration?: string;
  /** Short SHA of the older head this case closed on; it is not evidence for the run's head. */
  olderHead?: string;
  /** Results on older heads, oldest first. */
  attempts?: ProofPanelAttempt[];
}

export interface ProofPanelAttempt {
  head?: string;
  status: ProofCaseStatus;
  observed?: string;
  reason?: ProofCaseReason;
}

export type ProofPanelFinding = ProofFinding & { olderHead?: string };

export type ProofPanelRun =
  | {
      phase: "starting";
      cases: ProofPanelCase[];
      findings: ProofPanelFinding[];
    }
  | {
      phase: "planning";
      cases: ProofPanelCase[];
      findings: ProofPanelFinding[];
    }
  | { phase: "running"; cases: ProofPanelCase[]; findings: ProofPanelFinding[] }
  | {
      phase: "finished";
      cases: ProofPanelCase[];
      findings: ProofPanelFinding[];
      /** Set when the run ended before the child reported, e.g. `interrupted`. */
      abortedReason?: string;
      summary?: string;
    };

const shortSha = (sha: string) => sha.slice(0, 8);

/** `"9f8e7d6c"` when `sha` is known and is not the head the run is proving. */
function olderHead(
  sha: string | null | undefined,
  runHead: string | null | undefined,
): string | undefined {
  return sha && runHead && sha !== runHead ? shortSha(sha) : undefined;
}

function panelCase(item: ProofCase, runHead?: string | null): ProofPanelCase {
  const started = item.started_at ? Date.parse(item.started_at) : NaN;
  const closed = item.closed_at ? Date.parse(item.closed_at) : NaN;
  const duration =
    Number.isFinite(started) && Number.isFinite(closed)
      ? formatDurationMs(closed - started)
      : undefined;
  return {
    id: item.id,
    title: item.title,
    claim: item.claim,
    hero: item.hero,
    status: item.status,
    observed: item.observed ?? undefined,
    reason: item.reason ?? undefined,
    evidence: item.evidence,
    duration,
    olderHead:
      item.status === "pending" || item.status === "running"
        ? undefined
        : olderHead(item.head_sha, runHead),
    attempts: item.attempts?.map((attempt) => ({
      head: attempt.head_sha ? shortSha(attempt.head_sha) : undefined,
      status: attempt.status,
      observed: attempt.observed ?? undefined,
      reason: attempt.reason ?? undefined,
    })),
  };
}

export function panelRunFromSnapshot(
  run: ProofRunSnapshot & { reply?: string | null },
): ProofPanelRun {
  const cases = run.cases.map((item) => panelCase(item, run.head_sha));
  const findings = run.findings.map((finding) => ({
    ...finding,
    olderHead: olderHead(finding.head_sha, run.head_sha),
  }));
  if (run.status === "finished" || run.status === "aborted") {
    return {
      phase: "finished",
      cases,
      findings,
      abortedReason: run.aborted_reason ?? undefined,
      summary: run.reply?.trim() || undefined,
    };
  }
  if (run.status === "running") return { phase: "running", cases, findings };
  return cases.length === 0
    ? { phase: "starting", cases, findings }
    : { phase: "planning", cases, findings };
}
